import { SettingSection } from '@/components/settings/SettingSection';
import { useEffect, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';

interface HomeAddressEditorProps {
  initialAddress: string;
  accentColor: string;
  textColor: string;
  placeholderColor: string;
  backgroundColor: string;
  onSave: (address: string) => void;
  isSaving?: boolean;
}

/**
 * HomeAddressEditor Component
 * Allows users to edit their home address (route planner start/end point).
 */
export function HomeAddressEditor({
  initialAddress,
  accentColor,
  textColor,
  placeholderColor,
  backgroundColor,
  onSave,
  isSaving = false,
}: HomeAddressEditorProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [homeAddress, setHomeAddress] = useState(initialAddress);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setHomeAddress(initialAddress);
  }, [initialAddress]);

  useEffect(() => {
    setHasChanges(homeAddress !== initialAddress);
  }, [homeAddress, initialAddress]);

  const handleSave = () => {
    if (hasChanges && !isSaving) {
      onSave(homeAddress.trim());
    }
  };

  return (
    <SettingSection title="HOME ADDRESS" textColor={textColor}>
      <View style={[styles.container, { backgroundColor }]}>
        <View style={styles.inputContainer}>
          <TextInput
            style={[styles.textInput, { color: textColor, backgroundColor: palette.cardBackground, borderColor: palette.border }]}
            value={homeAddress}
            onChangeText={setHomeAddress}
            placeholder="Enter your home address"
            placeholderTextColor={placeholderColor}
            editable={!isSaving}
            maxLength={300}
          />
        </View>

        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[
              styles.saveButton,
              { backgroundColor: accentColor },
              (!hasChanges || isSaving) && styles.saveButtonDisabled,
            ]}
            disabled={!hasChanges || isSaving}
            onPress={handleSave}
            activeOpacity={0.7}>
            <Text style={styles.saveButtonText}>
              {isSaving ? 'Saving...' : 'Save'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </SettingSection>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BorderRadius.md,
    padding: Spacing.lg,
  },
  inputContainer: {
    marginBottom: Spacing.md,
  },
  textInput: {
    fontSize: 16,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.md,
    borderWidth: 1,
  },
  buttonContainer: {
    marginBottom: Spacing.sm,
  },
  saveButton: {
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.xxl,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.4,
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
